<template>
  <div class="ppInfo">
    <div class="projectInfo">
      <p class="projectInfo_title bold">
        {{ $t('workPermit.projectinfo') }}
      </p>
      <div
        v-if="currentWorkPermit.isTenantProject"
        class="project-info__content"
      >
        <div class="projectInfo_box">
          <p class="projectInfo_box-title">
            {{ $t('workPermit.tenant') }}
          </p>
          <p class="projectInfo_box-content">
            {{ company && company.name || $t('emptyValue') }}
          </p>
        </div>
        <div class="projectInfo_box">
          <p class="projectInfo_box-title">
            {{ $t('mainContractor') }}
          </p>
          <p class="projectInfo_box-content">
            {{ mainContractor || $t('emptyValue') }}
          </p>
        </div>
        <div
          v-if="building"
          class="projectInfo_box"
        >
          <p class="projectInfo_box-title">
            {{ $t('workPermit.building') }}
          </p>
          <p class="projectInfo_box-content">
            {{ building }}
          </p>
        </div>
        <div class="projectInfo_box">
          <p class="projectInfo_box-title">
            {{ $t('tenancy.floor') }}
          </p>
          <p class="projectInfo_box-content">
            {{ project.floors ? project.floors.join(', ') : $t('emptyValue') }}
          </p>
        </div>
        <div class="projectInfo_box">
          <p class="projectInfo_box-title">
            {{ $t('tenancyProject.projectPremises') }}
          </p>
          <p class="projectInfo_box-content">
            {{ showLocation }}
          </p>
        </div>
        <div class="projectInfo_box">
          <p class="projectInfo_box-title">
            {{ $t('tenancy.type') }}
          </p>
          <p class="projectInfo_box-content">
            {{ tenancyTypeDisplay }}
          </p>
        </div>
        <div class="projectInfo_box">
          <p class="projectInfo_box-title">
            {{ $t('workPermit.projectDes') }}
          </p>
          <p class="projectInfo_box-content">
            {{ project.description || $t('emptyValue') }}
          </p>
        </div>
      </div>
      <div
        v-else
        class="project-info__content"
      >
        <div class="projectInfo_box">
          <p class="projectInfo_box-title">
            {{ isPo ? $t('project.po.number') : $t('project.contract.number') }}
          </p>
          <p class="projectInfo_box-content">
            {{ project.number || $t('emptyValue') }}
          </p>
        </div>
        <div class="projectInfo_box">
          <p class="projectInfo_box-title">
            {{ $t('tender.general.projectDescription') }}
          </p>
          <p class="projectInfo_box-content">
            {{ poDescription }}
          </p>
        </div>
        <div class="projectInfo_box vendor-name">
          <p class="projectInfo_box-title">
            {{ $t('mainContractorName') }}
          </p>
          <p class="projectInfo_box-content ">
            {{ vendorName }}
          </p>
        </div>
        <div class="projectInfo_box">
          <p class="projectInfo_box-title">
            {{ $t('project.purchaseOrder.site') }}
          </p>
          <p class="projectInfo_box-content">
            {{ vendorSite }}
          </p>
        </div>
        <div class="projectInfo_box">
          <p class="projectInfo_box-title">
            {{ $t('workPermit.building') }}
          </p>
          <p class="projectInfo_box-content">
            {{ building }}
          </p>
        </div>
        <div
          v-if="hadDlpDate"
          class="projectInfo_box work-permit-dlp-date"
        >
          <p class="projectInfo_box-title">
            {{ $t('project.workPermitDlpDate') }}
          </p>
          <p class="projectInfo_box-content">
            {{ dlpDate }}
          </p>
        </div>
      </div>
    </div>
    <div
      v-if="isTenantProject && currentWorkPermit.type === 'WORK'"
      class="mainContratorInfo"
    >
      <p class="mainContratorInfo_title bold">
        {{ $t('workPermit.mainContractorWorkPermit') }}
      </p>
      <div class="mainContratorInfo_content">
        <div class="mainContratorInfo_duration">
          <p class="bold">
            {{ $t('workPermit.duration') }}
          </p>
          <div class="content">
            {{ pp.duration || $t('emptyValue') }}
          </div>
        </div>
        <div class="mainContratorInfo_status">
          <p class="bold">
            {{ $t('tenancyProject.status') }}
          </p>
          <div class="content">
            {{ pp.status | customStatusLabel }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters } from 'vuex'

import dateUtil from '@/utils/dateUtil'
import { getPurchase } from '@/api/projects/util'
import { PROJECT_TYPES } from '@/modules/projects/consts'
import { WORK_PERMIT_TYPES } from './consts'

export default {
  name: 'ProjectInfo',
  props: {
    projectPermit: {
      type: [String, Object],
      default: () => {},
    },
  },
  computed: {
    ...mapGetters('workPermit', ['currentWorkPermit', 'buildingFloorZoneMap']),
    ...mapGetters('tenantProject', ['currentTenantProject']),
    ...mapGetters('projects', ['currentProject']),
    ...mapGetters('user', ['isTenant']),
    isTenantProject () {
      return this.currentWorkPermit.isTenantProject
    },
    tenancyTypeDisplay () {
      const type = this.project.type || ''
      return this.$t(`tenancyProject.${type.toLowerCase()}`) || this.$t('emptyValue')
    },
    project () {
      const hklProject = lodash.get(this, 'currentProject')
      return this.isTenantProject
        ? lodash.get(this, 'currentTenantProject.tenancy') ? lodash.get(this, 'currentTenantProject') : lodash.get(this.currentWorkPermit, 'project', {})
        : Object.keys(hklProject).length ? hklProject : lodash.get(this.currentWorkPermit, 'project', {})
    },
    company () {
      return this.isTenantProject ? lodash.get(this, 'project.tenancy.company', {}) : {}
    },
    pp () {
      if (!lodash.isEmpty(this.projectPermit)) {
        this.projectPermit.duration = dateUtil.formatDate(this.projectPermit.startDate, true) + ' - ' + dateUtil.formatDate(this.projectPermit.endDate, true)
      }
      return this.projectPermit || {}
    },
    isPo () {
      return this.project.numberType === PROJECT_TYPES.PO
    },
    mainContractor () {
      return this.currentWorkPermit.type === WORK_PERMIT_TYPES.PROJECT
        ? lodash.get(this.currentWorkPermit, 'contractor.name')
        : lodash.get(this.projectPermit, 'contractor.name', this.$t('emptyValue'))
    },
    building () {
      if (this.isTenantProject) {
        return lodash.get(this.currentTenantProject, 'tenancy.building', {}).fullName
      } else {
        let buildings = lodash.get(this.project, 'buildings', [])
        buildings = buildings.length > 0 ? buildings : lodash.get(this.project, 'site.buildings', [])
        return buildings.map(building => building.fullName).join(', ')
      }
    },
    showLocation () {
      return !lodash.isEmpty(this.project.projectLocation) ? this.project.projectLocation.join(', ') : this.$t('emptyValue')
    },
    purchase () {
      return getPurchase(this.project)
    },
    vendorName () {
      return lodash.get(this.purchase, 'vendor.name', this.$t('emptyValue'))
    },
    vendorSite () {
      return lodash.get(this.purchase, 'siteId', this.$t('emptyValue'))
    },
    poDescription () {
      return lodash.get(this.purchase, 'description', this.$t('emptyValue'))
    },
    dlpDate () {
      return !this.currentProject.dlpDate ? this.$t('emptyValue') : dateUtil.formatDate(this.currentProject.dlpDate)
    },
    hadDlpDate () {
      return !!this.currentProject.dlpDate
    },
  },
}
</script>

<style lang='scss'>
.ppInfo {
  margin-bottom: 15px;
  display: flex;
  .bold {
    font-weight: bold;
  }
}
.mainContratorInfo {
  padding: 15px;
  margin-left: 20px;
  background: #F2F6F8;
  width: 380px;
  .mainContratorInfo_content {
    display: flex;
    .mainContratorInfo_title {
      font-size: 16px;
      height: 30px;
      line-height: 30px;
      margin-bottom: 10px;
    }
    .mainContratorInfo_duration {
      flex: 4;
    }
    .mainContratorInfo_status {
      flex: 2;
      margin-left: 15px;
    }
  }
  .bold {
    font-weight: bold;
  }
}
.projectInfo {
  padding: 15px;
  flex: 1;
  background: #F2F6F8;
  .project-info__content {
    display: flex;
  }
  p {
    margin-bottom: 0;
  }
  .projectInfo_title {
    font-size: 16px;
    height: 30px;
    line-height: 30px;
    margin-bottom: 10px;
  }
  .projectInfo_box {
    display: inline-block;
    max-width: 200px;
    margin-right: 30px;
    margin-bottom: 10px;
    vertical-align: top;
    .projectInfo_box-title {
      font-weight: bold;
      margin-bottom: 5px;
      text-transform: capitalize;
    }
    .projectInfo_box-content {
      min-height: 40px;
    }
    &.vendor-name {
      max-width: 500px;
    }
    &.work-permit-dlp-date {
      max-width: 400px;
    }
  }
}
.project-field {
  .emphasis {
    margin-top: 0;
  }
}
</style>
